import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AlertCircle, CheckCircle2, Database, RefreshCw, Save } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { SITE_CONTENT_DEFAULTS, SITE_CONTENT_SECTIONS } from '../../data/siteContentDefaults';
import { saveSiteContent, useSiteContent } from '../../hooks/useSiteContent';

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--white);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
`;

const SidebarHeader = styled.div`
  padding: 1.2rem 1.25rem;
  border-bottom: 1px solid var(--border-light);
  font-weight: 700;
  color: var(--text-dark);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SectionList = styled.div`
  max-height: calc(100vh - 260px);
  overflow-y: auto;
`;

const SectionButton = styled.button`
  width: 100%;
  text-align: left;
  border: none;
  background: ${props => props.$active ? 'rgba(0, 38, 100, 0.06)' : 'transparent'};
  border-left: ${props => props.$active ? '3px solid var(--primary-blue)' : '3px solid transparent'};
  color: ${props => props.$active ? 'var(--primary-blue)' : 'var(--text-dark)'};
  padding: 0.95rem 1rem;
  cursor: pointer;
  transition: var(--transition);

  .label {
    font-weight: 700;
    margin-bottom: 0.25rem;
  }

  .desc {
    font-size: 0.84rem;
    color: var(--text-light);
    line-height: 1.45;
  }

  &:hover {
    background: var(--background-light);
  }
`;

const EditorHeader = styled.div`
  padding: 1.2rem 1.25rem;
  border-bottom: 1px solid var(--border-light);

  h2 {
    font-family: var(--font-secondary);
    font-size: 1.25rem;
    color: var(--text-dark);
    margin-bottom: 0.45rem;
  }

  p {
    font-size: 0.92rem;
    color: var(--text-light);
  }
`;

const EditorBody = styled.div`
  padding: 1rem 1.25rem 1.25rem;
`;

const InfoBar = styled.div`
  margin-bottom: 0.9rem;
  padding: 0.8rem 0.9rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;

  &.error {
    color: #991b1b;
    background: #fee2e2;
  }

  &.ok {
    color: #065f46;
    background: #d1fae5;
  }

  &.note {
    color: #1e3a8a;
    background: #dbeafe;
  }
`;

const EditorArea = styled.textarea`
  width: 100%;
  min-height: 62vh;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 1rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  font-size: 0.9rem;
  line-height: 1.6;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
    box-shadow: 0 0 0 3px rgba(0, 38, 100, 0.1);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: var(--radius-md);
  padding: 0.75rem 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: var(--transition);
  border: 1px solid transparent;

  &.primary {
    background: var(--primary-blue);
    color: var(--white);
  }

  &.primary:hover {
    background: #004d8f;
  }

  &.secondary {
    background: var(--background-light);
    border-color: var(--border-light);
    color: var(--text-dark);
  }

  &.secondary:hover {
    background: var(--off-white);
  }
`;

const SectionKey = styled.code`
  display: inline-block;
  font-size: 0.8rem;
  background: var(--off-white);
  color: var(--text-dark);
  border: 1px solid var(--border-light);
  border-radius: 6px;
  padding: 0.2rem 0.5rem;
  margin-top: 0.5rem;
`;

const AdminParametres = () => {
  const [activeKey, setActiveKey] = useState(SITE_CONTENT_SECTIONS[0].key);
  const [editorValue, setEditorValue] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const { content, loading, error } = useSiteContent(activeKey);
  const currentSection = useMemo(
    () => SITE_CONTENT_SECTIONS.find(s => s.key === activeKey),
    [activeKey]
  );

  useEffect(() => {
    if (loading) return;
    const value = JSON.stringify(content ?? SITE_CONTENT_DEFAULTS[activeKey], null, 2);
    setEditorValue(value);
    setDirty(false);
    setJsonError('');
  }, [activeKey, content, loading]);

  const handleChangeSection = (nextKey) => {
    setActiveKey(nextKey);
    setStatusMessage('');
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(editorValue);
      setEditorValue(JSON.stringify(parsed, null, 2));
      setJsonError('');
    } catch (err) {
      setJsonError(`JSON invalide: ${err.message}`);
    }
  };

  const handleReset = () => {
    const fallback = SITE_CONTENT_DEFAULTS[activeKey];
    setEditorValue(JSON.stringify(fallback, null, 2));
    setDirty(true);
    setJsonError('');
    setStatusMessage('');
  };

  const handleSave = async () => {
    setJsonError('');
    setStatusMessage('');

    let parsed;
    try {
      parsed = JSON.parse(editorValue);
    } catch (err) {
      setJsonError(`JSON invalide: ${err.message}`);
      return;
    }

    setIsSaving(true);
    const result = await saveSiteContent(activeKey, parsed);
    setIsSaving(false);

    if (result.success) {
      setStatusMessage('Contenu enregistre avec succes.');
      setDirty(false);
      return;
    }

    setJsonError(result.error || "Erreur lors de l'enregistrement.");
  };

  return (
    <AdminLayout title="Parametres dynamiques">
      <PageGrid>
        <Card>
          <SidebarHeader>
            <Database size={18} />
            Sections dynamiques
          </SidebarHeader>
          <SectionList>
            {SITE_CONTENT_SECTIONS.map((section) => (
              <SectionButton
                key={section.key}
                $active={section.key === activeKey}
                onClick={() => handleChangeSection(section.key)}
              >
                <div className="label">{section.label}</div>
                <div className="desc">{section.description}</div>
              </SectionButton>
            ))}
          </SectionList>
        </Card>

        <Card>
          <EditorHeader>
            <h2>{currentSection?.label}</h2>
            <p>{currentSection?.description}</p>
            <SectionKey>{activeKey}</SectionKey>
          </EditorHeader>

          <EditorBody>
            {loading && (
              <InfoBar className="note">
                <RefreshCw size={16} />
                Chargement du contenu...
              </InfoBar>
            )}

            {error && (
              <InfoBar className="error">
                <AlertCircle size={16} />
                {error}
              </InfoBar>
            )}

            {jsonError && (
              <InfoBar className="error">
                <AlertCircle size={16} />
                {jsonError}
              </InfoBar>
            )}

            {statusMessage && (
              <InfoBar className="ok">
                <CheckCircle2 size={16} />
                {statusMessage}
              </InfoBar>
            )}

            <InfoBar className="note">
              <AlertCircle size={16} />
              Edition en JSON. Verifiez la structure avant enregistrement. Les pages publiques utilisent ce contenu en temps reel.
            </InfoBar>

            <EditorArea
              value={editorValue}
              onChange={(e) => {
                setEditorValue(e.target.value);
                setDirty(true);
              }}
              spellCheck={false}
            />

            <Actions>
              <Button className="primary" onClick={handleSave} disabled={isSaving || !dirty}>
                <Save size={16} />
                {isSaving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
              <Button className="secondary" onClick={handleFormat}>
                <RefreshCw size={16} />
                Formatter le JSON
              </Button>
              <Button className="secondary" onClick={handleReset}>
                Reinitialiser avec le modele
              </Button>
            </Actions>
          </EditorBody>
        </Card>
      </PageGrid>
    </AdminLayout>
  );
};

export default AdminParametres;

